import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Swal from "sweetalert2";
import {
    Card, CardBody, Container, Row, Col, Label,
} from "reactstrap";
import { getTakeLenses, getTakeLensesByClinic, getClinic, deleteTakeLens } from "service/api";

const THEME = "#DC4C64";

const cardTopStyle = {
    backgroundColor: THEME,
    padding: "6px 12px",
    color: "white",
    borderRadius: "4px 4px 0 0",
    fontWeight: 700,
    fontSize: 13,
};

const COATING_LABELS = {
    uc: "UC",
    hc: "HC",
    hmc_blue: "HMC Blue",
    shmc_blue: "SHMC Blue",
    photo: "Photo",
};

const Listtakelens = () => {
    const navigate = useNavigate();
    const [lenses, setLenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clinicIdFromToken, setClinicIdFromToken] = useState(null);
    const [allcliniclist, setAllcliniclist] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // ── Decode token ──
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setClinicIdFromToken(decoded?.clinicID || null);
            } catch (e) { /* ignore */ }
        }
    }, []);

    // ── Admin: load clinic list ──
    useEffect(() => {
        if (!clinicIdFromToken) {
            getClinic().then(res => setAllcliniclist(res?.data || [])).catch(() => { });
        }
    }, [clinicIdFromToken]);

    // ── Fetch records ──
    const fetchLenses = async (clinicId) => {
        setLoading(true);
        try {
            let res;
            if (clinicId) {
                res = await getTakeLensesByClinic(clinicId);
            } else {
                res = await getTakeLenses();
            }
            setLenses(res?.data || []);
        } catch (e) {
            setLenses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clinicIdFromToken) {
            fetchLenses(clinicIdFromToken);
        } else {
            fetchLenses(null);
        }
    }, [clinicIdFromToken]);

    const handleClinicFilter = (e) => {
        const val = e.target.value;
        setSelectedClinic(val);
        setCurrentPage(1);
        fetchLenses(val || null);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Delete this lens record?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME,
            cancelButtonColor: "#aaa",
            confirmButtonText: "Yes, delete",
        }).then(async (result) => {
            if (result.isConfirmed) {
                await deleteTakeLens(id);
                setLenses(prev => prev.filter(l => l._id !== id));
                Swal.fire("Deleted!", "Lens record removed.", "success");
            }
        });
    };

    const getPatientName = (l) => {
        const p = l.visit_id?.patientid;
        if (p?.firstname || p?.lastname) {
            return `${p.title ? p.title + ". " : ""}${p.firstname || ""} ${p.lastname || ""}`.trim();
        }
        const v = l.visit_id;
        if (v?.firstname || v?.lastname) {
            return `${v.title ? v.title + ". " : ""}${v.firstname || ""} ${v.lastname || ""}`.trim();
        }
        return null;
    };

    const filtered = lenses.filter(l => {
        const s = search.toLowerCase();
        const visitNo = (l.visit_no || "").toLowerCase();
        const matOD = (l.lens_material_od || l.lens_material || "").toLowerCase();
        const matOS = (l.lens_material_os || l.lens_material || "").toLowerCase();
        const visionOD = (l.lens_vision_od?.vision || l.lens_vision?.vision || "").toLowerCase();
        const visionOS = (l.lens_vision_os?.vision || l.lens_vision?.vision || "").toLowerCase();
        const subOD = (l.lens_subvision_od?.sub_type || l.lens_subvision?.sub_type || "").toLowerCase();
        const subOS = (l.lens_subvision_os?.sub_type || l.lens_subvision?.sub_type || "").toLowerCase();
        const patient = (getPatientName(l) || "").toLowerCase();
        return visitNo.includes(s) || matOD.includes(s) || matOS.includes(s)
            || visionOD.includes(s) || visionOS.includes(s)
            || subOD.includes(s) || subOS.includes(s) || patient.includes(s);
    });

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Toolbar */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="page-title" style={{ margin: 0 }}>Lens Records</h4>
                        <Link to="/takelense" className="btn btn-danger">
                            <i className="mdi mdi-plus me-1" />Add Lens
                        </Link>
                    </div>
                    <Card>
                        <div style={cardTopStyle}>Lens Selection Records</div>
                        <CardBody>
                            {/* Filters */}
                            <Row className="mb-3">
                                {!clinicIdFromToken && (
                                    <Col md={4}>
                                        <Label>Filter by Practice</Label>
                                        <select
                                            className="form-control"
                                            value={selectedClinic}
                                            onChange={handleClinicFilter}
                                        >
                                            <option value="">All Practices</option>
                                            {allcliniclist.map(c => (
                                                <option key={c._id} value={c._id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </Col>
                                )}
                                <Col md={4}>
                                    <Label>Search</Label>
                                    <input
                                        className="form-control"
                                        placeholder="Visit no., patient, material, vision, coating..."
                                        value={search}
                                        onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                    />
                                </Col>
                            </Row>
                            {/* Table */}
                            {loading ? (
                                <div className="text-center py-4">
                                    <span className="spinner-border text-danger" />
                                </div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center py-4 text-muted">No lens records found.</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                                        <thead style={{ background: "#fff5f7" }}>
                                            <tr>
                                                <th style={{ color: "#000", fontWeight: 700 }}>#</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Visit No.</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Patient</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Practice</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Material</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Vision</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Sub-Type</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Photochromic</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Date</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.map((l, i) => {
                                                const globalIndex = (currentPage - 1) * PAGE_SIZE + i;
                                                const clinic = l.visit_id?.patientid?.clinicname || l.clinic_id;

                                                const matOD = l.lens_material_od || l.lens_material;
                                                const matOS = l.lens_material_os || l.lens_material;
                                                const visionOD = l.lens_vision_od?.vision || l.lens_vision?.vision;
                                                const visionOS = l.lens_vision_os?.vision || l.lens_vision?.vision;
                                                const subOD = l.lens_subvision_od?.sub_type || l.lens_subvision?.sub_type;
                                                const subOS = l.lens_subvision_os?.sub_type || l.lens_subvision?.sub_type;
                                                const coatOD = l.lens_type_od || l.lens_type;
                                                const coatOS = l.lens_type_os || l.lens_type;
                                                const photoOD = (l.extra_od || l.extra) === "yes";
                                                const photoOS = (l.extra_os || l.extra) === "yes";

                                                const eyeCell = (od, os, renderFn) => (
                                                    <div style={{ lineHeight: 1.7 }}>
                                                        <div><span style={{ fontSize: 10, fontWeight: 700, color: THEME, marginRight: 4 }}>OD</span>{renderFn ? renderFn(od) : (od || "—")}</div>
                                                        <div><span style={{ fontSize: 10, fontWeight: 700, color: "#5b9bd5", marginRight: 4 }}>OS</span>{renderFn ? renderFn(os) : (os || "—")}</div>
                                                    </div>
                                                );

                                                return (
                                                    <tr key={l._id}>
                                                        <td style={{ color: "#888" }}>{globalIndex + 1}</td>
                                                        <td>
                                                            <span style={{ fontWeight: 700, color: THEME }}>
                                                                {l.visit_no || l.visit_id?.visit_no || "—"}
                                                            </span>
                                                        </td>
                                                        <td>{getPatientName(l) || "—"}</td>
                                                        <td>{clinic?.name || "—"}</td>
                                                        <td>{eyeCell(matOD, matOS)}</td>
                                                        <td>{eyeCell(visionOD, visionOS)}</td>
                                                        <td>{eyeCell(subOD, subOS)}</td>

                                                        <td>
                                                            {eyeCell(photoOD, photoOS, (val) =>
                                                                val
                                                                    ? <span style={{ background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: 4, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>Yes</span>
                                                                    : <span style={{ color: "#bbb", fontSize: 11 }}>No</span>
                                                            )}
                                                        </td>
                                                        <td style={{ color: "#888", fontSize: 11, whiteSpace: "nowrap" }}>
                                                            {l.createdAt ? new Date(l.createdAt).toLocaleDateString("en-GB") : "—"}
                                                        </td>
                                                        <td style={{ whiteSpace: "nowrap" }}>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => navigate(`/view-takelens/${l._id}`)}
                                                                title="View"
                                                            >
                                                                <i className="mdi mdi-eye" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDelete(l._id)}
                                                                title="Delete"
                                                            >
                                                                <i className="mdi mdi-delete" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {!loading && filtered.length > PAGE_SIZE && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span style={{ fontSize: 13, color: "#888" }}>
                                        Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} records
                                    </span>
                                    <ul className="pagination mb-0" style={{ gap: 3 }}>
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(1)}>«</button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                                        </li>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                            .reduce((acc, p, idx, arr) => {
                                                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                                acc.push(p);
                                                return acc;
                                            }, [])
                                            .map((p, idx) =>
                                                p === "..." ? (
                                                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                                                        <span className="page-link" style={{ borderColor: "#dee2e6" }}>…</span>
                                                    </li>
                                                ) : (
                                                    <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                        <button
                                                            className="page-link"
                                                            style={currentPage === p ? { backgroundColor: THEME, borderColor: THEME, color: "#fff" } : { color: THEME, borderColor: "#dee2e6" }}
                                                            onClick={() => setCurrentPage(p)}
                                                        >{p}</button>
                                                    </li>
                                                )
                                            )}
                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(totalPages)}>»</button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Listtakelens;
